import { Box, Button, CircularProgress, IconButton, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { AxiosError, AxiosResponse } from 'axios';
import Divider from 'components/divider';
import Drawer, { DrawerHandle } from 'components/drawer';
import LoadingSpinner from 'components/loading-spinner';
import NumberField from 'components/number-field';
import TextField from 'components/text-field';
import { SnackbarActionTypes, useSnackbarDispatch } from 'contexts/snackbar/SnackbarContext';
import { usePost } from 'hooks/use-post';
import { MinusCircle, PlusCircle } from 'lucide-react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, useFieldArray, useForm, useWatch } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { ErrorMessage } from 'types/shared/error-message.type';
import { isValidInteger } from 'utils/validate-numbers';
import { useOrderFeedState } from 'views/order-feed/context/order-feed-context';
import { useOrderFeedSettingsState } from 'views/order-feed/context/order-feed-settings-context';
import { deleteOrder as deleteOrderRequest } from 'views/order-feed/features/my-supply-plan/api/delete-order';
import EditOrderDate from 'views/order-feed/features/my-supply-plan/components/edit-order-date';
import EditProductAutocomplete from 'views/order-feed/features/my-supply-plan/components/edit-product-autocomplete';
import { useMySupplyPlanState } from 'views/order-feed/features/my-supply-plan/context/my-supply-plan-context';
import OrderFormHeader from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/components/orders/form/order-header';
import { calculateTotalOrderQuantity } from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/utils/total-quantity-counter';
import { useGoogleTagManager } from 'views/order-feed/features/my-supply-plan/hooks/use-google-tag-manager';
import { useUpdateOrder } from 'views/order-feed/features/my-supply-plan/hooks/use-update-order';
import { DeleteOrderInput } from 'views/order-feed/features/my-supply-plan/types/delete-order-input.type';
import { EditOrderFormValues } from 'views/order-feed/features/my-supply-plan/types/edit-order-form-values.type';
import { SupplyPlanType } from 'views/order-feed/features/my-supply-plan/types/google-tag-manager/order-feed-events.type';
import { buildEditOrderInput } from 'views/order-feed/features/my-supply-plan/utils/edit-order-utils';
import { ChangeableStatus } from 'views/order-feed/types/changeable-status.enum';

export type EditOrderDialogOrder = Pick<
  EditOrderFormValues,
  | 'orderNumber'
  | 'transportMethod'
  | 'requestedDispatchDate'
  | 'siteErpId'
  | 'orderLines'
  | 'orderComment'
  | 'customerOrderReference'
  | 'plantId'
  | 'vessel'
> & {
  customerErpId: string;
  totalOrderQuantity: number;
  changeableStatus?: ChangeableStatus;
};

type Props = {
  order: EditOrderDialogOrder;
  onSuccess?: () => void;
  onDeleteSuccess?: () => void;
};

const PAGE_NAME_ID = 'orderFeed.editOrder';
const ORDERS_PAGE_NAME_ID = 'orderFeed.ofdMySupplyPlan.orders';

const EditOrderDialogTitle: React.FC<{ orderNumber: string }> = ({ orderNumber }) => (
  <Box display="flex" alignItems="center" gap={3}>
    <Typography fontWeight="bold">
      <FormattedMessage id="general.edit" values={{ type: '' }} />
    </Typography>
    <Typography>#{orderNumber}</Typography>
  </Box>
);

const DeleteOrderConfirmation: React.FC<{
  isLoading: boolean;
  onCancel: () => void;
  onDelete: () => void;
}> = ({ isLoading, onCancel, onDelete }) => (
  <Box display="flex" flexDirection="column" gap={4}>
    <Typography fontWeight={500} textAlign="center">
      <FormattedMessage id={`${ORDERS_PAGE_NAME_ID}.modals.confirmedDialog.deleteOrder.deleteOrderMessage`} />
    </Typography>
    <Box display="flex" justifyContent="space-between">
      <Button onClick={onDelete} disabled={isLoading} startIcon={isLoading ? <CircularProgress color="inherit" size={18} /> : null}>
        <FormattedMessage id={`${ORDERS_PAGE_NAME_ID}.modals.confirmedDialog.deleteButton`} />
      </Button>
      <Button onClick={onCancel} variant="outlined" disabled={isLoading}>
        <FormattedMessage id="general.cancel" />
      </Button>
    </Box>
  </Box>
);

const EditOrderDialog: React.FC<Props> = ({ order, onSuccess, onDeleteSuccess }) => {
  const intl = useIntl();
  const drawerRef = useRef<DrawerHandle>(null);
  const queryClient = useQueryClient();
  const {
    orderSettingsQuery: { data: orderSettings },
  } = useOrderFeedSettingsState();
  const snackbarDispatch = useSnackbarDispatch();
  const { createOrderFeedEvent } = useGoogleTagManager();
  const {
    dispatchDatesQuery: { data: dispatchSchedule, isLoading: isDispatchScheduleLoading },
  } = useOrderFeedState();
  const {
    customerAssortmentQuery: { data: customerProducts, isLoading: isProductsLoading },
  } = useMySupplyPlanState();
  const { handleUpdateOrder, updateOrder: updateOrderMutation } = useUpdateOrder();
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);

  const defaultValues = useMemo<EditOrderFormValues>(
    () => ({
      orderNumber: order.orderNumber,
      transportMethod: order.transportMethod,
      requestedDispatchDate: order.requestedDispatchDate,
      siteErpId: order.siteErpId,
      orderLines: order.orderLines,
      orderComment: order.orderComment,
      customerOrderReference: order.customerOrderReference,
      plantId: order.plantId,
      vessel: order.vessel,
    }),
    [order]
  );

  const form = useForm<EditOrderFormValues>({
    defaultValues,
    mode: 'onChange',
    reValidateMode: 'onChange',
    shouldUnregister: false,
  });
  const { handleSubmit, reset, control, formState } = form;

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  const { fields, remove, append } = useFieldArray({
    control,
    name: 'orderLines',
    shouldUnregister: false,
  });

  const watchOrderLines = useWatch({
    control,
    name: 'orderLines',
    defaultValue: fields,
  });

  const totalOrderQuantity = useMemo(() => calculateTotalOrderQuantity(watchOrderLines), [watchOrderLines]);

  const updateOrder = (data: EditOrderFormValues) => {
    handleUpdateOrder(buildEditOrderInput(data), {
      onSuccess: () => {
        [['getOrders'], ['getOrder', order.orderNumber]].forEach((queryKey) => {
          queryClient.invalidateQueries({ queryKey });
        });
        drawerRef.current?.close();
        onSuccess?.();
      },
    });

    const newTotalOrderQuantity = data.orderLines.reduce((total, line) => total + line.orderQuantity, 0);
    createOrderFeedEvent('update', {
      orderNumber: data.orderNumber,
      supplyPlanType: SupplyPlanType.OFD1,
      oldValue1: String(order.totalOrderQuantity),
      newValue1: String(newTotalOrderQuantity),
    });
  };

  const deleteOrder = usePost<AxiosResponse, { orderNumber: string; deleteOrderInput: DeleteOrderInput }, AxiosError<ErrorMessage[]>>(
    (request) => deleteOrderRequest(request.orderNumber, request.deleteOrderInput),
    () => {
      snackbarDispatch({
        type: SnackbarActionTypes.SUCCESS_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.success.default' }) },
      });
      [['getOrders'], ['getOrder', order.orderNumber]].forEach((queryKey) => {
        queryClient.invalidateQueries({ queryKey: queryKey});
      });
      drawerRef.current?.close();
      onDeleteSuccess?.();
    },
    (error) => {
      snackbarDispatch({
        type: SnackbarActionTypes.ERROR_SNACKBAR,
        payload: {
          msg: intl.formatMessage({ id: 'snackbar.errors.default' }),
          error,
          errorMsg: error.response?.data,
        },
      });
    }
  );

  const isMutationLoading = updateOrderMutation.isPending || deleteOrder.isPending;

  return (
    <Drawer
      ref={drawerRef}
      onClose={() => setIsConfirmingDelete(false)}
      title={<EditOrderDialogTitle orderNumber={order.orderNumber} />}
      button={
        <Button
          size="small"
          variant="outlined"
          disabled={orderSettings?.isUnderMaintenance}
          onClick={() => {
            createOrderFeedEvent('edit', {
              orderNumber: order.orderNumber,
              supplyPlanType: SupplyPlanType.OFD1,
            });
          }}>
          <FormattedMessage id={'general.edit'} values={{ type: '' }} />
        </Button>
      }>
      <Box display="flex" flexDirection="column" width={650}>
        {isConfirmingDelete ? (
          <DeleteOrderConfirmation
            isLoading={deleteOrder.isPending}
            onCancel={() => setIsConfirmingDelete(false)}
            onDelete={() => {
              if (order.customerErpId) {
                createOrderFeedEvent('delete', {
                  orderNumber: order.orderNumber,
                  supplyPlanType: SupplyPlanType.OFD1,
                });
                deleteOrder.mutate({
                  orderNumber: order.orderNumber,
                  deleteOrderInput: { customerErpId: order.customerErpId },
                });
              }
            }}
          />
        ) : (
          <FormProvider {...form}>
            <form onSubmit={handleSubmit(updateOrder)}>
              <Box display="flex" flexDirection="column" gap={4}>
                <OrderFormHeader value={totalOrderQuantity} orderTitle={`${ORDERS_PAGE_NAME_ID}.totalOrderQuantity`} />
                <EditOrderDate shippingSchedule={dispatchSchedule} isLoading={isDispatchScheduleLoading} />
                <Divider dark />
                <Box display="flex" flexDirection="column" gap={2}>
                  {isProductsLoading ? (
                    <LoadingSpinner />
                  ) : (
                    fields.map((field, idx) => (
                      <Box display="flex" gap={2} key={field.id} alignItems="flex-start">
                        <EditProductAutocomplete idx={idx} products={customerProducts} disabled={isMutationLoading} />
                        <NumberField
                          dark
                          label={intl.formatMessage({ id: `${PAGE_NAME_ID}.orderLinesList.orderQuantityLabel` })}
                          disableDecimal
                          endAdornment="kg"
                          controller={{
                            name: `orderLines.${idx}.orderQuantity`,
                            control,
                            rules: {
                              required: intl.formatMessage({ id: 'form.rules.required' }),
                              validate: {
                                positive: (value) =>
                                  isValidInteger(value) && Number(value) > 0
                                    ? true
                                    : intl.formatMessage({ id: 'form.rules.positive' }),
                              },
                            },
                          }}
                        />
                        <Box display="flex">
                          <IconButton
                            color="primary"
                            disabled={watchOrderLines.length <= 1 || isMutationLoading}
                            title={intl.formatMessage({ id: `${PAGE_NAME_ID}.orderLinesList.removeOrderLine` })}
                            onClick={() => remove(idx)}>
                            <MinusCircle size={40} strokeWidth={1} />
                          </IconButton>
                          {idx === 0 && (
                            <IconButton
                              color="primary"
                              disabled={isMutationLoading}
                              title={intl.formatMessage({ id: `${PAGE_NAME_ID}.orderLinesList.addOrderLine` })}
                              onClick={() => append({ itemNumber: '', orderQuantity: 0 })}>
                              <PlusCircle size={40} strokeWidth={1} />
                            </IconButton>
                          )}
                        </Box>
                      </Box>
                    ))
                  )}
                </Box>
                <Divider dark />
                <Box display="flex" flexDirection="column" gap={4}>
                  <TextField
                    fullWidth
                    dark
                    label={intl.formatMessage({ id: 'orderFeed.listOrderSupplyPlan.orderList.createOrder.customerReference' })}
                    maxLength={20}
                    controller={{ name: 'customerOrderReference', control }}
                  />
                  <TextField
                    fullWidth
                    dark
                    multiline
                    label={intl.formatMessage({ id: `${PAGE_NAME_ID}.orderLinesList.orderCommentLabel` })}
                    controller={{ name: 'orderComment', control }}
                  />
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Button type="button" variant="outlined" disabled={isMutationLoading} onClick={() => setIsConfirmingDelete(true)}>
                    <FormattedMessage id="general.delete" />
                  </Button>
                  <Button
                    type="submit"
                    disabled={
                      isMutationLoading ||
                      orderSettings?.isUnderMaintenance ||
                      order.changeableStatus === ChangeableStatus.NOT_CHANGEABLE
                    }
                    startIcon={updateOrderMutation.isPending ? <CircularProgress color="inherit" size={18} /> : null}>
                    <FormattedMessage id="general.save" />
                  </Button>
                </Box>
              </Box>
            </form>
          </FormProvider>
        )}
      </Box>
    </Drawer>
  );
};

export default EditOrderDialog;
